import type { TaxEntity } from "../../domain/entities/TaxEntity";
import type { ChangeDetails } from "../../utils/reviewWorkflow";
import { getEffectiveTotalFromRawJson } from "./invoiceBillEffectiveTotal";
import {
  getReverseChargeContextFromJson,
  shouldShowReverseChargeMessage,
} from "../reverseCharge";
import { buildInvoiceBillDisplayDiff } from "./buildInvoiceBillDisplayDiff";
import type {
  DisplayDiffItem,
  InvoiceOrBillKind,
} from "./invoiceBillDisplayDiff.types";
import { getHeaderAllowlist, getLineAllowlist } from "./invoiceBillAllowlists";
import { formatInvoiceBillReviewSummarySections } from "./invoiceBillChangeSummaryLines";
import type {
  InvoiceBillHeaderLike,
  InvoiceBillLineLike,
} from "./invoiceBillDisplayValues";
import {
  getDisplayValueForLineField,
  isGstinHeaderFieldKey,
  normalizeBlankishGstinDisplay,
} from "./invoiceBillDisplayValues";
import { formatActionTypesList } from "./reviewActionLabels";
import { isBillTrackedFieldChangeRowAllowed } from "./billPendingReviewDisplayMaps";
import { isInvoiceTrackedFieldChangeRowAllowed } from "./invoicePendingReviewDisplayMaps";
import { formatInvoiceTrackingFieldName } from "./invoicePendingReviewDisplayMaps";
import { formatDateDDMMYYYY } from "../formatters";

export type FieldChangeRow = {
  label: string;
  oldText: string;
  newText: string;
};

export type LineItemChangeGroup = {
  lineNumber: number;
  title: string;
  changes: FieldChangeRow[];
};

export type GLOverrideGroup = {
  lineNumber: number;
  title: string;
  accountChange: FieldChangeRow;
  reason: string;
};

export type ReviewChangesViewModel = {
  documentKind: InvoiceOrBillKind;
  headerChanges: FieldChangeRow[];
  lineItemChanges: LineItemChangeGroup[];
  glOverrideSections: GLOverrideGroup[];
  totalImpact: {
    subTotal?: FieldChangeRow;
    taxTotal?: FieldChangeRow;
    grandTotal?: FieldChangeRow;
    tcsTotal?: FieldChangeRow;
    reverseCharge?: {
      show: boolean;
      amountFormatted: string;
    };
  };
  stats: {
    changeCount: number;
    affectedLineItems: number;
    totalDifferenceFormatted?: string;
  };
  actionTypes: string[];
  showRecalculatedNote: boolean;
  isEmpty: boolean;
  currencySymbol: string;
};

const HEADER_TOTAL_KEYS = new Set(["sub_total", "tax_total", "total"]);
const DATE_FIELD_KEYS = new Set(["date", "due_date"]);

/** Format review change dates as dd-mm-yyyy (field_changes often use yyyy-mm-dd). */
function formatReviewChangeDateText(text: string): string {
  const t = String(text ?? "").trim();
  if (!t || t === "—") return t;

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (isoMatch) {
    return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }

  const formatted = formatDateDDMMYYYY(t);
  return formatted || t;
}

function formatFieldChangeRowText(fieldKey: string, text: string): string {
  if (!DATE_FIELD_KEYS.has(fieldKey)) return text;
  return formatReviewChangeDateText(text);
}

function displayItemToFieldChangeRow(item: DisplayDiffItem): FieldChangeRow {
  return {
    label: item.label,
    oldText: formatFieldChangeRowText(item.fieldKey, item.oldText),
    newText: formatFieldChangeRowText(item.fieldKey, item.newText),
  };
}
const LINE_FIELD_ORDER = [
  "description",
  "hsn_or_sac",
  "quantity",
  "rate",
  "item_total",
  "tax_name",
  "tax_percentage",
  "tax_amount",
  "account_name",
  "tds_tax_name",
  "tds_tax_percentage",
] as const;

const LINE_TOTAL_INCLUSIVE_KEY = "item_total_inclusive_of_tax";

const LINE_ITEM_FIELD_RE = /^Line Item (\d+): (.+)$/i;

function hasSnapshotPair(
  changeDetails: ChangeDetails | null | undefined,
): boolean {
  const o = changeDetails?.original_json;
  const r = changeDetails?.raw_json;
  return (
    o != null && r != null && typeof o === "object" && typeof r === "object"
  );
}

function resolveLineKeyFromLabel(
  kind: InvoiceOrBillKind,
  subfieldLabel: string,
): string | undefined {
  const fields = getLineAllowlist(kind);
  for (const { key, label } of fields) {
    if (subfieldLabel === label) return key;
    if (subfieldLabel === formatInvoiceTrackingFieldName(key)) return key;
  }
  return undefined;
}

function resolveHeaderKeyFromField(
  kind: InvoiceOrBillKind,
  field: string,
): string | undefined {
  const fields = getHeaderAllowlist(kind);
  for (const { key, label } of fields) {
    if (field === label) return key;
    if (field === formatInvoiceTrackingFieldName(key)) return key;
    if (key === "customer_name" && field === "Customer") return key;
    if (key === "vendor_name" && field === "Vendor") return key;
  }
  return undefined;
}

function fieldChangeToDisplayItem(
  kind: InvoiceOrBillKind,
  fc: { field: string; old: string; new: string },
): DisplayDiffItem | null {
  const lineMatch = LINE_ITEM_FIELD_RE.exec(fc.field);
  if (lineMatch) {
    const lineNumber = parseInt(lineMatch[1], 10);
    const subfield = lineMatch[2].trim();
    const fieldKey = resolveLineKeyFromLabel(kind, subfield);
    if (!fieldKey) return null;
    const label =
      getLineAllowlist(kind).find((f) => f.key === fieldKey)?.label ?? subfield;
    return {
      scope: "line",
      lineNumber,
      fieldKey,
      label,
      oldText: fc.old,
      newText: fc.new,
    };
  }

  const headerKey = resolveHeaderKeyFromField(kind, fc.field);
  if (!headerKey) return null;
  const label =
    getHeaderAllowlist(kind).find((f) => f.key === headerKey)?.label ??
    fc.field;
  let oldText = fc.old;
  let newText = fc.new;
  if (isGstinHeaderFieldKey(headerKey)) {
    oldText = normalizeBlankishGstinDisplay(fc.old);
    newText = normalizeBlankishGstinDisplay(fc.new);
    if (oldText === newText) return null;
  }
  return {
    scope: "header",
    fieldKey: headerKey,
    label,
    oldText,
    newText,
  };
}

function mergeDisplayDiffItems(
  snapshotItems: DisplayDiffItem[],
  fieldChangeItems: DisplayDiffItem[],
): DisplayDiffItem[] {
  const keyOf = (d: DisplayDiffItem) =>
    d.scope === "line"
      ? `line:${d.lineNumber}:${d.fieldKey}`
      : `header:${d.fieldKey}`;

  const map = new Map<string, DisplayDiffItem>();
  for (const item of snapshotItems) {
    map.set(keyOf(item), item);
  }
  for (const item of fieldChangeItems) {
    map.set(keyOf(item), item);
  }
  return Array.from(map.values());
}

function buildMergedDisplayItems(
  kind: InvoiceOrBillKind,
  changeDetails: ChangeDetails,
  taxes?: TaxEntity[],
): DisplayDiffItem[] {
  let snapshotItems: DisplayDiffItem[] = [];

  if (hasSnapshotPair(changeDetails)) {
    const orig = changeDetails.original_json as InvoiceBillHeaderLike & {
      line_items?: InvoiceBillLineLike[];
    };
    const next = changeDetails.raw_json as InvoiceBillHeaderLike & {
      line_items?: InvoiceBillLineLike[];
    };
    const vs = changeDetails.validation_snapshot;
    const validationSnapshot =
      vs && Array.isArray(vs.line_items) && vs.line_items.length > 0
        ? {
            line_items: vs.line_items as InvoiceBillLineLike[],
            tax_total: vs.tax_total,
            sub_total: vs.sub_total,
            total: vs.total,
          }
        : null;

    snapshotItems = buildInvoiceBillDisplayDiff({
      kind,
      originalHeader: orig,
      newHeader: next,
      originalLineItems: orig?.line_items,
      newLineItems: next?.line_items,
      taxes,
      validationSnapshot,
      // Validation GST/TDS never appear here; user tax edits come via field_changes.
      omitValidationOwnedLineFields: true,
    });
  }

  const allow =
    kind === "invoice"
      ? isInvoiceTrackedFieldChangeRowAllowed
      : isBillTrackedFieldChangeRowAllowed;

  const isVendorGstMetadataField = (field: string) => {
    const f = field.trim().toLowerCase();
    return (
      f === "gst_treatment" ||
      f === "gst_category" ||
      f === "gst treatment" ||
      f === "gst category"
    );
  };

  const fieldChangeItems = (changeDetails.field_changes ?? [])
    .filter((fc) => !isVendorGstMetadataField(fc.field))
    .filter((fc) => allow(fc.field))
    .map((fc) => fieldChangeToDisplayItem(kind, fc))
    .filter((item): item is DisplayDiffItem => item != null);

  if (snapshotItems.length > 0 && fieldChangeItems.length > 0) {
    const hasLineItemFc = (changeDetails.field_changes ?? []).some((c) =>
      /^Line Item\s+\d+/i.test(String(c.field || "").trim()),
    );
    if (hasLineItemFc) {
      snapshotItems = snapshotItems.filter((d) => d.scope !== "line");
    }
  }

  return mergeDisplayDiffItems(snapshotItems, fieldChangeItems);
}

function parseTaxPercentage(line: InvoiceBillLineLike | undefined): number {
  if (!line) return 0;
  const tp = line.tax_percentage;
  if (typeof tp === "number" && !Number.isNaN(tp)) return tp;
  const tid = String(line.tax_id ?? "").trim();
  if (tid === "NO_TAX") return 0;
  const parsed = parseFloat(String(tp ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDisplayPercent(text: string): string {
  const t = text.trim();
  if (!t) return t;
  return t.endsWith("%") ? t : `${t}%`;
}

function isLineNoTax(
  kind: InvoiceOrBillKind,
  line: InvoiceBillLineLike | undefined,
  taxes?: TaxEntity[],
): boolean {
  if (!line) return false;
  if (trimLineText(line.tax_id).toUpperCase() === "NO_TAX") return true;
  const taxName = getDisplayValueForLineField(kind, "tax_name", line, taxes);
  return taxName.trim().toLowerCase() === "no tax";
}

function formatTaxPercentageDisplayText(
  diffText: string,
  line: InvoiceBillLineLike | undefined,
  kind: InvoiceOrBillKind,
  taxes?: TaxEntity[],
): string {
  if (isLineNoTax(kind, line, taxes)) return "No Tax";
  const t = trimLineText(diffText);
  if (t.toLowerCase() === "no tax") return "No Tax";
  if (!t || t === "empty") return t;
  return formatDisplayPercent(t);
}

function trimLineText(v: unknown): string {
  return v === null || v === undefined ? "" : String(v).trim();
}

function resolveTaxKindFromLine(
  line: InvoiceBillLineLike | undefined,
): "GST" | "IGST" | null {
  if (!line) return null;
  const name = trimLineText(line.tax_name).toUpperCase();
  const id = trimLineText(line.tax_id).toUpperCase();
  const combined = `${name} ${id}`;
  if (combined.includes("IGST")) return "IGST";
  if (combined.includes("GST")) return "GST";
  return null;
}

function resolveTaxPercentageLabel(
  oldLine: InvoiceBillLineLike | undefined,
  newLine: InvoiceBillLineLike | undefined,
): string {
  for (const line of [newLine, oldLine]) {
    const kind = resolveTaxKindFromLine(line);
    if (kind === "IGST") return "IGST %";
    if (kind === "GST") return "GST %";
  }
  return "GST %";
}

function resolveLineTaxNameForDisplay(
  kind: InvoiceOrBillKind,
  line: InvoiceBillLineLike | undefined,
  taxes?: TaxEntity[],
): string {
  if (!line) return "";
  const rawName = trimLineText(line.tax_name);
  if (rawName) return rawName;
  return getDisplayValueForLineField(kind, "tax_name", line, taxes);
}

function coalesceTaxNameText(
  diffText: string,
  line: InvoiceBillLineLike | undefined,
  kind: InvoiceOrBillKind,
  taxes?: TaxEntity[],
): string {
  const t = trimLineText(diffText);
  if (t && t !== "empty" && t.toLowerCase() !== "no tax") {
    return t;
  }
  return resolveLineTaxNameForDisplay(kind, line, taxes);
}

function selectLineItemTaxRows(
  lineNumber: number,
  items: DisplayDiffItem[],
  oldLine: InvoiceBillLineLike | undefined,
  newLine: InvoiceBillLineLike | undefined,
  kind: InvoiceOrBillKind,
  taxes?: TaxEntity[],
): FieldChangeRow[] {
  const byKey = new Map(
    items
      .filter((d) => d.scope === "line" && d.lineNumber === lineNumber)
      .map((d) => [d.fieldKey, d]),
  );

  const changedKeys = new Set(byKey.keys());
  const qtyOrRateChanged =
    changedKeys.has("quantity") ||
    changedKeys.has("rate") ||
    changedKeys.has("item_total");

  const taxDrivingFields = ["tax_name", "tax_percentage", "tax_id"] as const;
  const taxPctOrNameChanged = taxDrivingFields.some((k) => changedKeys.has(k));

  const oldTaxPct = parseTaxPercentage(oldLine);
  const newTaxPct = parseTaxPercentage(newLine);

  const rows: FieldChangeRow[] = [];
  const taxFieldKeys = ["tax_name", "tax_percentage", "tax_amount"] as const;
  const pctLabel = resolveTaxPercentageLabel(oldLine, newLine);

  for (const key of taxFieldKeys) {
    const item = byKey.get(key);
    if (!item) continue;

    const oldText =
      key === "tax_name"
        ? coalesceTaxNameText(item.oldText, oldLine, kind, taxes)
        : key === "tax_percentage"
          ? formatTaxPercentageDisplayText(item.oldText, oldLine, kind, taxes)
          : item.oldText;
    const newText =
      key === "tax_name"
        ? coalesceTaxNameText(item.newText, newLine, kind, taxes)
        : key === "tax_percentage"
          ? formatTaxPercentageDisplayText(item.newText, newLine, kind, taxes)
          : item.newText;

    if (oldText !== newText) {
      rows.push({
        label: key === "tax_percentage" ? pctLabel : item.label,
        oldText,
        newText,
      });
    }
  }

  const resolvedOldTaxName = resolveLineTaxNameForDisplay(kind, oldLine, taxes);
  const resolvedNewTaxName = resolveLineTaxNameForDisplay(kind, newLine, taxes);
  const hasTaxNameRow = rows.some(
    (r) => r.label.toLowerCase() === "tax name",
  );
  if (
    !hasTaxNameRow &&
    resolvedOldTaxName !== resolvedNewTaxName &&
    (resolvedOldTaxName || resolvedNewTaxName)
  ) {
    rows.unshift({
      label: "Tax name",
      oldText: resolvedOldTaxName,
      newText: resolvedNewTaxName,
    });
  }

  const includeTaxAmount =
    byKey.has("tax_amount") ||
    taxPctOrNameChanged ||
    (qtyOrRateChanged && (oldTaxPct > 0 || newTaxPct > 0));

  if (includeTaxAmount && !rows.some((r) => r.label === "Tax Amount")) {
    const oldAmt = getDisplayValueForLineField(
      kind,
      "tax_amount",
      oldLine,
      taxes,
    );
    const newAmt = getDisplayValueForLineField(
      kind,
      "tax_amount",
      newLine,
      taxes,
    );
    if (oldAmt !== newAmt) {
      rows.push({
        label: "Tax Amount",
        oldText: oldAmt,
        newText: newAmt,
      });
    }
  }

  return rows;
}

function orderLineFieldRows(
  lineNumber: number,
  items: DisplayDiffItem[],
  oldLine: InvoiceBillLineLike | undefined,
  newLine: InvoiceBillLineLike | undefined,
  kind: InvoiceOrBillKind,
  taxes: TaxEntity[] | undefined,
  excludeAccount: boolean,
  gstTreatment?: string,
  gstCategory?: string,
): FieldChangeRow[] {
  const byKey = new Map(
    items
      .filter((d) => d.scope === "line" && d.lineNumber === lineNumber)
      .map((d) => [d.fieldKey, d]),
  );

  const taxRows = selectLineItemTaxRows(
    lineNumber,
    items,
    oldLine,
    newLine,
    kind,
    taxes,
  );
  const taxKeys = new Set(["tax_name", "tax_percentage", "tax_amount"]);

  const rows: FieldChangeRow[] = [];

  for (const key of LINE_FIELD_ORDER) {
    if (taxKeys.has(key)) continue;
    if (excludeAccount && key === "account_name") continue;

    const item = byKey.get(key);
    if (!item || item.oldText === item.newText) continue;
    rows.push({
      label: item.label,
      oldText: item.oldText,
      newText: item.newText,
    });
  }

  const taxInsertIndex = rows.findIndex((r) =>
    ["Total without tax", "Rate", "Quantity"].includes(r.label),
  );
  if (taxRows.length > 0) {
    if (taxInsertIndex >= 0) {
      const afterQtyRate = rows.findIndex(
        (r, i) =>
          i > taxInsertIndex &&
          (r.label === "Total without tax" || r.label === "Rate"),
      );
      const insertAt = afterQtyRate >= 0 ? afterQtyRate + 1 : rows.length;
      rows.splice(insertAt, 0, ...taxRows);
    } else {
      rows.push(...taxRows);
    }
  }

  const inclusiveItem = byKey.get(LINE_TOTAL_INCLUSIVE_KEY);
  let inclusiveOld = inclusiveItem?.oldText ?? "";
  let inclusiveNew = inclusiveItem?.newText ?? "";
  if (!inclusiveItem || inclusiveOld === inclusiveNew) {
    const fromOld = getDisplayValueForLineField(
      kind,
      LINE_TOTAL_INCLUSIVE_KEY,
      oldLine,
      taxes,
    );
    const fromNew = getDisplayValueForLineField(
      kind,
      LINE_TOTAL_INCLUSIVE_KEY,
      newLine,
      taxes,
    );
    if (fromOld !== fromNew) {
      inclusiveOld = fromOld;
      inclusiveNew = fromNew;
    }
  }
  const skipInclusiveTotal =
    kind === "bill" &&
    (shouldShowReverseChargeMessage(gstTreatment, gstCategory, [
      newLine ?? {},
    ]) ||
      shouldShowReverseChargeMessage(gstTreatment, gstCategory, [
        oldLine ?? {},
      ]));

  if (
    !skipInclusiveTotal &&
    inclusiveOld !== inclusiveNew &&
    (inclusiveOld || inclusiveNew)
  ) {
    const inclusiveLabel =
      getLineAllowlist(kind).find((f) => f.key === LINE_TOTAL_INCLUSIVE_KEY)
        ?.label ?? "Total after adding tax";
    rows.push({
      label: inclusiveLabel,
      oldText: inclusiveOld,
      newText: inclusiveNew,
    });
  }

  return rows;
}

function getGLOverrideLineIndices(changeDetails: ChangeDetails): Set<number> {
  const indices = new Set<number>();
  if (Array.isArray(changeDetails.line_item_gl_data)) {
    changeDetails.line_item_gl_data.forEach(
      (gl: { line_item_index?: number }) => {
        if (gl.line_item_index !== undefined) {
          indices.add(gl.line_item_index);
        }
      },
    );
  } else if (
    changeDetails.line_item_gl_data &&
    changeDetails.line_item_index !== undefined
  ) {
    indices.add(changeDetails.line_item_index);
  }
  return indices;
}

function getLineTitle(
  lineNumber: number,
  rawJson: InvoiceBillHeaderLike | undefined,
): string {
  const lines = rawJson?.line_items as InvoiceBillLineLike[] | undefined;
  const line = lines?.[lineNumber - 1];
  const desc = line?.description;
  if (typeof desc === "string" && desc.trim()) return desc.trim();
  return `Line Item ${lineNumber}`;
}

function formatCurrencyDelta(delta: number, symbol: string): string {
  const sign = delta >= 0 ? "" : "-";
  return `${sign}${symbol}${Math.abs(delta).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export interface BuildInvoiceBillReviewChangesViewModelParams {
  kind: InvoiceOrBillKind;
  changeDetails: ChangeDetails | null | undefined;
  taxes?: TaxEntity[];
  requiresReview: boolean;
  actionCode?: string;
  resolvedGstTreatment?: string;
  resolvedGstCategory?: string;
}

export function buildInvoiceBillReviewChangesViewModel(
  params: BuildInvoiceBillReviewChangesViewModelParams,
): ReviewChangesViewModel {
  const {
    kind,
    changeDetails,
    taxes,
    requiresReview,
    actionCode,
    resolvedGstTreatment,
    resolvedGstCategory,
  } = params;

  const empty: ReviewChangesViewModel = {
    documentKind: kind,
    headerChanges: [],
    lineItemChanges: [],
    glOverrideSections: [],
    totalImpact: {},
    stats: { changeCount: 0, affectedLineItems: 0 },
    actionTypes: formatActionTypesList(changeDetails?.action_types, actionCode),
    showRecalculatedNote: false,
    isEmpty: true,
    currencySymbol: "₹",
  };

  if (!changeDetails) return empty;

  const rawJson = changeDetails.raw_json as InvoiceBillHeaderLike | undefined;
  const origJson = changeDetails.original_json as
    | InvoiceBillHeaderLike
    | undefined;
  const currencySymbol =
    String(
      rawJson?.currency_symbol ?? origJson?.currency_symbol ?? "₹",
    ).trim() || "₹";

  const vendorGstContext = getReverseChargeContextFromJson(
    rawJson as Record<string, unknown> | undefined,
    resolvedGstTreatment,
    resolvedGstCategory,
  );
  const effectiveGstTreatment = vendorGstContext.gstTreatment;
  const effectiveGstCategory = vendorGstContext.gstCategory;

  const { systemChanges } = formatInvoiceBillReviewSummarySections(
    kind,
    changeDetails,
    taxes,
  );

  const displayItems = buildMergedDisplayItems(kind, changeDetails, taxes);

  const isPermanentGLOverrideReview =
    requiresReview &&
    (changeDetails.action_types?.includes("override_gl_permanent") ?? false);

  const glOverrideIndices = isPermanentGLOverrideReview
    ? getGLOverrideLineIndices(changeDetails)
    : new Set<number>();

  const origLines = (origJson?.line_items ?? []) as InvoiceBillLineLike[];
  const vsLines = changeDetails.validation_snapshot?.line_items as
    | InvoiceBillLineLike[]
    | undefined;
  const effectiveOrigLines =
    vsLines && vsLines.length > 0 ? vsLines : origLines;
  const newLines = (rawJson?.line_items ?? []) as InvoiceBillLineLike[];

  const headerChanges: FieldChangeRow[] = displayItems
    .filter((d) => d.scope === "header" && !HEADER_TOTAL_KEYS.has(d.fieldKey))
    .map(displayItemToFieldChangeRow);

  const glOverrideSections: GLOverrideGroup[] = [];
  const lineItemChanges: LineItemChangeGroup[] = [];

  const lineNumbers = new Set<number>();
  displayItems.forEach((d) => {
    if (d.scope === "line" && d.lineNumber != null) {
      lineNumbers.add(d.lineNumber);
    }
  });

  const sortedLineNumbers = Array.from(lineNumbers).sort((a, b) => a - b);

  for (const lineNum of sortedLineNumbers) {
    const lineIndex = lineNum - 1;

    if (glOverrideIndices.has(lineIndex)) {
      const glData = Array.isArray(changeDetails.line_item_gl_data)
        ? changeDetails.line_item_gl_data.find(
            (g: { line_item_index?: number }) =>
              g.line_item_index === lineIndex,
          )
        : changeDetails.line_item_gl_data;

      const accountItem = displayItems.find(
        (d) =>
          d.scope === "line" &&
          d.lineNumber === lineNum &&
          d.fieldKey === "account_name",
      );

      const oldAccount =
        (glData as { old_account_name?: string })?.old_account_name ??
        accountItem?.oldText ??
        "";
      const newAccount =
        (glData as { new_account_name?: string })?.new_account_name ??
        accountItem?.newText ??
        "";

      const reason =
        (glData as { reason?: string })?.reason ?? changeDetails.reason ?? "";

      glOverrideSections.push({
        lineNumber: lineNum,
        title: getLineTitle(lineNum, rawJson),
        accountChange: {
          label: "Account",
          oldText: oldAccount,
          newText: newAccount,
        },
        reason: String(reason).trim(),
      });

      const otherChanges = orderLineFieldRows(
        lineNum,
        displayItems,
        effectiveOrigLines[lineIndex],
        newLines[lineIndex],
        kind,
        taxes,
        true,
        effectiveGstTreatment,
        effectiveGstCategory,
      );
      if (otherChanges.length > 0) {
        lineItemChanges.push({
          lineNumber: lineNum,
          title: getLineTitle(lineNum, rawJson),
          changes: otherChanges,
        });
      }
      continue;
    }

    const changes = orderLineFieldRows(
      lineNum,
      displayItems,
      effectiveOrigLines[lineIndex],
      newLines[lineIndex],
      kind,
      taxes,
      false,
      effectiveGstTreatment,
      effectiveGstCategory,
    );

    if (changes.length > 0) {
      lineItemChanges.push({
        lineNumber: lineNum,
        title: getLineTitle(lineNum, rawJson),
        changes,
      });
    }
  }

  const totalImpact: ReviewChangesViewModel["totalImpact"] = {};
  for (const key of ["sub_total", "tax_total", "total"] as const) {
    const item = displayItems.find(
      (d) => d.scope === "header" && d.fieldKey === key,
    );
    if (!item) continue;
    const row: FieldChangeRow = {
      label:
        key === "sub_total"
          ? "Subtotal"
          : key === "tax_total"
            ? "GST Total"
            : "Grand Total",
      oldText: item.oldText,
      newText: item.newText,
    };
    if (key === "sub_total") totalImpact.subTotal = row;
    else if (key === "tax_total") totalImpact.taxTotal = row;
    else totalImpact.grandTotal = row;
  }

  const rawForTcs = rawJson as Record<string, unknown> | undefined;
  const rcNewContext = getReverseChargeContextFromJson(
    rawForTcs,
    effectiveGstTreatment,
    effectiveGstCategory,
  );
  if (
    rawForTcs?.tcs_tax_id &&
    rawForTcs.tcs_tax_id !== "" &&
    Number(rawForTcs.tcs_tax_percentage) > 0
  ) {
    const sub = Number(rawForTcs.sub_total) || 0;
    const tax = Number(rawForTcs.tax_total) || 0;
    const tcsBase = rcNewContext.showMessage ? sub : sub + tax;
    const tcsAmt =
      (tcsBase * (Number(rawForTcs.tcs_tax_percentage) || 0)) / 100;
    const tcsLabel = rawForTcs.tcs_tax_name
      ? `TCS Total (${rawForTcs.tcs_tax_name})`
      : "TCS Total";
    totalImpact.tcsTotal = {
      label: tcsLabel,
      oldText: "—",
      newText: tcsAmt.toFixed(2),
    };
  }

  const rcOldContext = getReverseChargeContextFromJson(
    origJson as Record<string, unknown> | undefined,
    effectiveGstTreatment,
    effectiveGstCategory,
  );
  // Overseas / RCM: payable Grand Total excludes tax. Only surface it when the
  // effective payable actually changed — never paint same→same (e.g. 100→100).
  // Do not inject a reverse-charge banner here: system-applied IGST/RCM is
  // omitted from change diffs (validation_snapshot), and a $0 banner is wrong
  // when RC tax exists only on lines / was system-applied.
  if (rcNewContext.showMessage || rcOldContext.showMessage) {
    const oldEffective = getEffectiveTotalFromRawJson(
      changeDetails.original_json as Record<string, unknown>,
      kind,
      effectiveGstTreatment,
      effectiveGstCategory,
    );
    const newEffective = getEffectiveTotalFromRawJson(
      changeDetails.raw_json as Record<string, unknown>,
      kind,
      effectiveGstTreatment,
      effectiveGstCategory,
    );
    if (Math.abs(newEffective - oldEffective) > 1e-6) {
      totalImpact.grandTotal = {
        label: "Grand Total",
        oldText: oldEffective.toFixed(2),
        newText: newEffective.toFixed(2),
      };
    } else if (
      totalImpact.grandTotal &&
      String(totalImpact.grandTotal.oldText ?? "").trim() ===
        String(totalImpact.grandTotal.newText ?? "").trim()
    ) {
      delete totalImpact.grandTotal;
    }
  }

  let changeCount =
    headerChanges.length +
    lineItemChanges.reduce((n, g) => n + g.changes.length, 0) +
    glOverrideSections.length;

  if (totalImpact.subTotal) changeCount += 1;
  if (totalImpact.taxTotal) changeCount += 1;
  if (totalImpact.grandTotal) changeCount += 1;

  const affectedLineItems = new Set([
    ...lineItemChanges.map((g) => g.lineNumber),
    ...glOverrideSections.map((g) => g.lineNumber),
  ]).size;

  let totalDifferenceFormatted: string | undefined;
  if (hasSnapshotPair(changeDetails)) {
    const oldTotal = getEffectiveTotalFromRawJson(
      changeDetails.original_json as Record<string, unknown>,
      kind,
      effectiveGstTreatment,
      effectiveGstCategory,
    );
    const newTotal = getEffectiveTotalFromRawJson(
      changeDetails.raw_json as Record<string, unknown>,
      kind,
      effectiveGstTreatment,
      effectiveGstCategory,
    );
    const delta = newTotal - oldTotal;
    if (Math.abs(delta) > 1e-6) {
      totalDifferenceFormatted = formatCurrencyDelta(delta, currencySymbol);
    }
  }

  const isEmpty =
    headerChanges.length === 0 &&
    lineItemChanges.length === 0 &&
    glOverrideSections.length === 0 &&
    !totalImpact.subTotal &&
    !totalImpact.taxTotal &&
    !totalImpact.grandTotal;

  return {
    documentKind: kind,
    headerChanges,
    lineItemChanges,
    glOverrideSections,
    totalImpact,
    stats: {
      changeCount,
      affectedLineItems,
      totalDifferenceFormatted,
    },
    actionTypes: formatActionTypesList(changeDetails.action_types, actionCode),
    showRecalculatedNote: systemChanges.length > 0,
    isEmpty,
    currencySymbol,
  };
}

function fieldChangeRowToApprovalLine(row: FieldChangeRow): string {
  const oldT = String(row.oldText ?? "").trim();
  const newT = String(row.newText ?? "").trim();
  const hasOld = oldT !== "" && oldT !== "—";
  if (!hasOld) return `${row.label}: ${newT}`;
  return `${row.label}: ${oldT} → ${newT}`;
}

/**
 * Flatten invoice/bill review view model into approval-panel text lines.
 * Uses gst_treatment / gst_category from raw_json (same logic as InvoiceBillReviewChangesModal).
 */
export function formatInvoiceBillReviewChangesForApprovalDisplay(
  kind: InvoiceOrBillKind,
  changeDetails: ChangeDetails | null | undefined,
  taxes?: TaxEntity[],
  actionCode?: string,
): string[] {
  const vm = buildInvoiceBillReviewChangesViewModel({
    kind,
    changeDetails,
    taxes,
    requiresReview: false,
    actionCode,
  });

  const lines: string[] = [];

  for (const row of vm.headerChanges) {
    lines.push(fieldChangeRowToApprovalLine(row));
  }

  const { totalImpact } = vm;
  if (totalImpact.subTotal) {
    lines.push(fieldChangeRowToApprovalLine(totalImpact.subTotal));
  }
  if (totalImpact.taxTotal) {
    lines.push(fieldChangeRowToApprovalLine(totalImpact.taxTotal));
  }
  if (totalImpact.tcsTotal) {
    lines.push(fieldChangeRowToApprovalLine(totalImpact.tcsTotal));
  }
  if (totalImpact.grandTotal) {
    const oldT = String(totalImpact.grandTotal.oldText ?? "").trim();
    const newT = String(totalImpact.grandTotal.newText ?? "").trim();
    if (oldT !== newT) {
      lines.push(fieldChangeRowToApprovalLine(totalImpact.grandTotal));
    }
  }

  for (const group of vm.lineItemChanges) {
    lines.push(`Line Item ${group.lineNumber}:`);
    for (const change of group.changes) {
      const oldT = String(change.oldText ?? "").trim();
      const newT = String(change.newText ?? "").trim();
      const hasOld = oldT !== "" && oldT !== "—";
      if (!hasOld) {
        lines.push(`  ${change.label}: ${newT}`);
      } else {
        lines.push(`  ${change.label}: ${oldT} → ${newT}`);
      }
    }
  }

  if (totalImpact.reverseCharge?.show) {
    lines.push(
      `Reverse charges of ${totalImpact.reverseCharge.amountFormatted} will be applied`,
    );
  }

  return lines.filter(
    (line) =>
      !line.startsWith("Customer GSTIN changed") &&
      !line.startsWith("Vendor GSTIN changed") &&
      !line.startsWith("Customer GSTIN:") &&
      !line.startsWith("Vendor GSTIN:"),
  );
}
